<template>
  <div class="d-flex vh-100 overflow-hidden position-relative main-layout">

    <!-- ===================== TOPBAR MOBILE (solo visible en móviles) ===================== -->
    <header class="mobile-topbar d-flex d-lg-none align-items-center justify-content-between px-3 py-2">
      <router-link to="/" class="d-flex align-items-center text-decoration-none">
        <div class="bg-electric rounded-2 p-1 me-2 shadow-sm d-flex align-items-center justify-content-center" style="width: 30px; height: 30px;">
          <i class="bi bi-gear-fill text-white" style="font-size: 0.85rem;"></i>
        </div>
        <span class="fw-extrabold text-white" style="font-size: 0.95rem; letter-spacing: -0.01em;">Sistema OT</span>
      </router-link>

      <button class="btn-hamburger" @click="toggleSidebar" aria-label="Abrir menú">
        <span :class="isSidebarOpen ? 'bi bi-x-lg' : 'bi bi-list'" class="bi"></span>
      </button>
    </header>

    <!-- ===================== SIDEBAR ===================== -->
    <SidebarMenu :isOpen="isSidebarOpen" @close="isSidebarOpen = false" />

    <!-- ===================== CONTENIDO PRINCIPAL ===================== -->
    <main class="flex-grow-1 d-flex flex-column min-vh-0 main-content-gradient main-area">
      <!-- Área scrollable para el contenido principal -->
      <div class="flex-grow-1 overflow-y-auto p-0">
        <!-- KeepAlive evita re-montar los componentes al navegar -->
        <router-view v-slot="{ Component }">
          <keep-alive :max="5">
            <component :is="Component" />
          </keep-alive>
        </router-view>
      </div>

      <!-- Barra de Estado GPS Silenciosa (Fuera del área de scroll para no tapar) -->
      <transition name="slide-up">
        <div v-if="maquinaStore.countMaquinasSinGps > 0" class="gps-status-bar shadow-lg" @click="$router.push('/maquinas')">
          <div class="d-flex align-items-center justify-content-between px-3 px-md-4 py-2 w-100">
            <div class="d-flex align-items-center flex-nowrap overflow-hidden">
              <div class="status-icon me-2 me-md-3 pulse-danger flex-shrink-0">
                <i class="bi bi-broadcast text-danger fs-5"></i>
              </div>
              <div class="status-text text-nowrap overflow-hidden">
                <span class="fw-bold me-1 title-text">GPS:</span>
                <span class="text-danger fw-extrabold">{{ maquinaStore.countMaquinasSinGps }} unidades</span>
                <span class="muted-text small ms-1 d-none d-md-inline text-opacity-75">sin señal hace más de 30 min</span>
              </div>
            </div>
            <div class="d-flex align-items-center flex-shrink-0 ms-2">
              <span class="badge bg-danger-subtle text-danger rounded-pill px-3 me-2 d-none d-sm-inline">Revisar detalles</span>
              <i class="bi bi-chevron-right text-danger"></i>
            </div>
          </div>
        </div>
      </transition>
    </main>

    <!-- Marca de agua -->
    <!-- <div class="watermark">
      CyM Contulmo 2026
    </div> -->
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import SidebarMenu from '../components/SidebarMenu.vue'
import { useThemeStore } from '../stores/theme'
import { useMaquinaStore } from '../stores/maquinas'
import { useNotificationStore } from '../stores/notificaciones'

const themeStore = useThemeStore()
const maquinaStore = useMaquinaStore()
const notificationStore = useNotificationStore()
const isSidebarOpen = ref(false)
const router = useRouter()

const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value
}

// Cerrar el drawer al navegar en mobile
router.afterEach(() => {
  isSidebarOpen.value = false
})

onMounted(() => {
  maquinaStore.init()
  notificationStore.init()
})
</script>

<style scoped>
/* ===================== LAYOUT BASE ===================== */
.main-layout {
  flex-direction: row;
}

@media (max-width: 991px) {
  .main-layout {
    flex-direction: column;
  }
}

.main-content-gradient {
  background: var(--bg-main);
  transition: background 0.5s ease;
}

/* El área de contenido ocupa el espacio restante */
.main-area {
  overflow: hidden;
}

/* ===================== TOPBAR MOBILE ===================== */
.mobile-topbar {
  background: var(--bg-sidebar);
  border-bottom: 1px solid rgba(14, 165, 233, 0.15);
  flex-shrink: 0;
  height: 56px;
  z-index: 1050;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.25);
}

.btn-hamburger {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 10px;
  color: #ffffff;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  cursor: pointer;
  transition: background 0.2s ease;
}

.btn-hamburger:hover {
  background: rgba(255, 255, 255, 0.12);
}

.bg-electric { background: var(--accent-gradient); }
.fw-extrabold { font-weight: 800; }

/* ===================== WATERMARK ===================== */
.watermark {
  position: fixed;
  bottom: 10px;
  right: 10px;
  font-family: 'Inter', sans-serif;
  font-size: 0.6rem;
  font-weight: 800;
  color: var(--text-muted, rgba(128, 128, 128, 0.15));
  pointer-events: none;
  user-select: none;
  z-index: 9999;
  text-transform: uppercase;
  letter-spacing: 0.2rem;
  transform: rotate(-5deg);
  mix-blend-mode: overlay;
}

/* ===================== BARRA GPS ===================== */
.gps-status-bar {
  background: linear-gradient(90deg, #991b1b 0%, #dc2626 50%, #991b1b 100%);
  z-index: 1000;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  border-top: 2px solid #fca5a5;
  backdrop-filter: blur(10px);
  min-height: 52px;
  display: flex;
  align-items: center;
  position: relative;
  box-shadow: 0 -4px 30px rgba(220, 38, 38, 0.4);
  /*animation: bar-glow 3s infinite;*/
}

@keyframes bar-glow {
  0% { box-shadow: 0 -4px 20px rgba(220, 38, 38, 0.4); }
  50% { box-shadow: 0 -8px 35px rgba(220, 38, 38, 0.6); }
  100% { box-shadow: 0 -4px 20px rgba(220, 38, 38, 0.4); }
}

.title-text {
  color: #ffffff !important;
  text-shadow: 0 1px 2px rgba(0,0,0,0.3);
}

.muted-text {
  color: rgba(255, 255, 255, 0.8) !important;
}

.gps-status-bar .text-danger {
  color: #ffffff !important;
  background: rgba(0,0,0,0.2);
  padding: 2px 8px;
  border-radius: 6px;
  margin: 0 4px;
}

.gps-status-bar .badge {
  background-color: #ffffff !important;
  color: #991b1b !important;
  font-weight: 800 !important;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
}

[data-theme='dark'] .gps-status-bar {
  background: linear-gradient(90deg, #450a0a 0%, #991b1b 50%, #450a0a 100%) !important;
  border-top: 2px solid #ef4444 !important;
}

.pulse-danger {
  /*animation: pulse-emergency 1.5s infinite;*/
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.2);
  width: 38px;
  height: 38px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(4px);
}

.pulse-danger i {
  color: #ffffff !important;
  font-size: 1.25rem;
}

@keyframes pulse-emergency {
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(250, 2, 2, 0.7); }
  70% { transform: scale(1.1); box-shadow: 0 0 0 12px rgba(250, 2, 2, 0); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(250, 2, 2, 0); }
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
}

.fw-extrabold { font-weight: 800; }
</style>
